import IosShareIcon from '@mui/icons-material/IosShare';
import {
  Backdrop,
  Box,
  CircularProgress,
  IconButton,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { NumericFormat } from 'react-number-format';
import {
  generateBookingConfirmationImage,
  generateBookingDepositRequestImage,
} from 'apis/booking.api';
import { Booking } from 'shared/models/booking.model';
import { OrderBill } from 'shared/models/orderBill.model';
import { BookingStatus } from 'shared/enums';
import { NotifyStatus } from 'shared/packages/myla-query/types';
import { useNotifyToast } from 'shared/packages/myla-query/hooks/useNotifyToast';
import SuiModal from 'components/SuiModal';

interface BookingConfirmationShareProps {
  booking: Booking;
  bills?: OrderBill[];
  totalAmount: number;
}

interface ConfirmationAmounts {
  totalAmount: number;
  deposit: number;
  remainingAmount: number;
}

interface PreparedShare {
  imageUrl: string;
  fileName: string;
  title: string;
  text: string;
  file?: File;
}

type NavigatorWithShare = Navigator & {
  canShare?: (data: ShareData) => boolean;
};

function getBookingGoogleMapUrl(booking: Booking) {
  const unitName = booking.unit?.name || '';
  if (unitName.toLowerCase().includes('bình dương')) {
    return 'https://maps.app.goo.gl/tJ7pvvz6NnuaawNv8?g_st=ic';
  }

  const mapValues = [
    booking.unit?.googleMapUrl?.trim(),
    booking.unit?.googleMapEmbedded?.trim(),
  ].filter(Boolean) as string[];
  const shareMapValue = mapValues.find((value) =>
    /https?:\/\/maps\.app\.goo\.gl\//i.test(value)
  );
  if (shareMapValue) {
    return (
      shareMapValue.match(/https?:\/\/maps\.app\.goo\.gl\/[^\s"'<>]+/i)?.[0] ||
      shareMapValue
    )
      .replaceAll('&amp;', '&')
      .replaceAll('&#38;', '&');
  }

  const coordinateMatch = mapValues
    .join(' ')
    .match(/!2d(-?\d+(?:\.\d+)?)!3d(-?\d+(?:\.\d+)?)/i);
  if (coordinateMatch) {
    const [, longitude, latitude] = coordinateMatch;
    return `https://www.google.com/maps?q=${latitude},${longitude}`;
  }

  const address = booking.unit?.address?.trim();
  return address
    ? `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
        address
      )}`
    : '';
}

function calculateBillsTotal(bills?: OrderBill[]) {
  return (
    bills?.reduce((sum, bill) => {
      const serviceCharge =
        bill.serviceCharge?.reduce(
          (total, item) => total + (item.value || 0),
          0
        ) || 0;
      return sum + (bill.totalAmount || 0) + serviceCharge;
    }, 0) || 0
  );
}

function downloadBlob(blob: Blob, fileName: string) {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = fileName;
  link.click();
  URL.revokeObjectURL(url);
}

async function prepareImageShare(
  imageUrl: string,
  fileName: string,
  title: string,
  text: string
): Promise<PreparedShare> {
  let blob: Blob | undefined;

  try {
    const response = await fetch(imageUrl);
    if (response.ok) blob = await response.blob();
  } catch (error) {
    blob = undefined;
  }

  return {
    imageUrl,
    fileName,
    title,
    text,
    file: blob
      ? new File([blob], fileName, { type: blob.type || 'image/jpeg' })
      : undefined,
  };
}

function sharePreparedImage(prepared: PreparedShare) {
  const nav = navigator as NavigatorWithShare;
  const { file, fileName, imageUrl, text, title } = prepared;

  if (file && nav.share) {
    const shareData: ShareData = { files: [file], title, text };
    if (!nav.canShare || nav.canShare(shareData)) {
      return nav.share(shareData);
    }
  }

  if (nav.share) {
    return nav.share({ title, text, url: imageUrl });
  }

  if (file) {
    downloadBlob(file, fileName);
  } else {
    window.open(imageUrl, '_blank', 'noopener,noreferrer');
  }

  return Promise.resolve();
}

function useFakeProgress(isActive: boolean) {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    if (!isActive) {
      setProgress(0);
      return undefined;
    }

    setProgress(3);
    const interval = window.setInterval(() => {
      setProgress((current) => {
        if (current >= 96) return current;
        const next = current + Math.floor(Math.random() * 11) + 3;
        return Math.min(next, 96);
      });
    }, 360);

    return () => window.clearInterval(interval);
  }, [isActive]);

  return [progress, setProgress] as const;
}

export default function BookingConfirmationShare({
  booking,
  bills,
  totalAmount,
}: BookingConfirmationShareProps) {
  const { showToast } = useNotifyToast();
  const [open, setOpen] = useState(false);
  const [shareReadyOpen, setShareReadyOpen] = useState(false);
  const [preparedShare, setPreparedShare] = useState<PreparedShare>();
  const [isGenerating, setIsGenerating] = useState(false);
  const [progress, setProgress] = useFakeProgress(isGenerating);
  const isDepositRequest = booking.status === BookingStatus.NEW;

  const defaultAmounts = useMemo<ConfirmationAmounts>(() => {
    const fallbackTotal = totalAmount || calculateBillsTotal(bills);
    const deposit = booking.deposit === 1 ? 0 : booking.deposit || 0;
    return {
      totalAmount: fallbackTotal,
      deposit,
      remainingAmount: Math.max(fallbackTotal - deposit, 0),
    };
  }, [bills, booking.deposit, totalAmount]);

  const [amounts, setAmounts] = useState<ConfirmationAmounts>(defaultAmounts);

  useEffect(() => {
    if (open) setAmounts(defaultAmounts);
  }, [defaultAmounts, open]);

  const canShareConfirmation = useMemo(() => {
    const unitName = booking.unit?.name || '';
    const distributorName = booking.distributor?.name || '';
    const bookingScope = `${unitName} ${distributorName}`.toLowerCase();
    return (
      bookingScope.includes('myla') ||
      bookingScope.includes('chi nhánh') ||
      bookingScope.includes('havana') ||
      bookingScope.includes('test')
    );
  }, [booking.distributor?.name, booking.unit?.name]);

  const updateAmount = useCallback(
    (key: keyof ConfirmationAmounts, value?: number) => {
      setAmounts((current) => {
        const next = { ...current, [key]: value || 0 };
        if (key === 'totalAmount' || key === 'deposit') {
          next.remainingAmount = Math.max(next.totalAmount - next.deposit, 0);
        }
        return next;
      });
    },
    []
  );

  const handleGenerate = useCallback(async () => {
    if (!booking._id) return;

    setIsGenerating(true);
    setProgress(8);

    try {
      const result = isDepositRequest
        ? await generateBookingDepositRequestImage(booking._id, {
            totalAmount: amounts.totalAmount,
            requestedDeposit: amounts.deposit,
          })
        : await generateBookingConfirmationImage(booking._id, amounts);
      const unitName = booking.unit?.name?.trim() || 'MyLa Home';
      const googleMapUrl = getBookingGoogleMapUrl(booking);
      const shareTitle = isDepositRequest
        ? `Yêu cầu cọc booking ${unitName}`
        : `Xác nhận booking ${unitName}`;
      const shareText = [
        shareTitle,
        googleMapUrl ? `Vị trí chi nhánh: ${googleMapUrl}` : '',
      ]
        .filter(Boolean)
        .join('\n');
      const prepared = await prepareImageShare(
        result.url,
        `${isDepositRequest ? 'phieu-yeu-cau-coc' : 'xac-nhan-booking'}-${
          booking.bookingCode || booking._id
        }.jpg`,
        shareTitle,
        shareText
      );
      setProgress(100);
      setPreparedShare(prepared);
      setOpen(false);
      setShareReadyOpen(true);
    } catch (error) {
      showToast({
        notifyStatus: NotifyStatus.ERROR,
        toastContent: {
          message:
            error instanceof Error
              ? error.message
              : isDepositRequest
              ? 'Không thể tạo phiếu yêu cầu cọc'
              : 'Không thể tạo ảnh xác nhận booking',
        },
      });
    } finally {
      window.setTimeout(() => setIsGenerating(false), 350);
    }
  }, [amounts, booking, isDepositRequest, setProgress, showToast]);

  const handleShare = useCallback(() => {
    if (!preparedShare) return;

    // Call navigator.share directly from this click to retain iOS user activation.
    const sharePromise = sharePreparedImage(preparedShare);
    sharePromise
      .then(() => setShareReadyOpen(false))
      .catch((error: unknown) => {
        if (error instanceof DOMException && error.name === 'AbortError')
          return;

        showToast({
          notifyStatus: NotifyStatus.ERROR,
          toastContent: {
            message:
              error instanceof DOMException && error.name === 'NotAllowedError'
                ? 'Trình duyệt đang chặn chia sẻ. Vui lòng mở trang bằng Safari hoặc Chrome và thử lại.'
                : error instanceof Error
                ? error.message
                : isDepositRequest
                ? 'Không thể chia sẻ phiếu yêu cầu cọc'
                : 'Không thể chia sẻ ảnh xác nhận booking',
          },
        });
      });
  }, [isDepositRequest, preparedShare, showToast]);

  if (!booking._id || !canShareConfirmation) return null;

  return (
    <>
      <Tooltip
        title={
          isDepositRequest ? 'Share yêu cầu cọc' : 'Share xác nhận booking'
        }
      >
        <IconButton
          aria-label={
            isDepositRequest ? 'Share yêu cầu cọc' : 'Share xác nhận booking'
          }
          onClick={() => {
            setPreparedShare(undefined);
            setOpen(true);
          }}
          sx={{
            width: 44,
            height: 44,
            flex: '0 0 auto',
            border: '1px solid',
            borderColor: 'info.main',
            color: 'info.main',
            bgcolor: 'background.paper',
            '&:hover': {
              bgcolor: 'info.main',
              color: 'background.paper',
            },
          }}
        >
          <IosShareIcon fontSize="small" />
        </IconButton>
      </Tooltip>

      <SuiModal
        open={open}
        onClose={() => setOpen(false)}
        title={
          isDepositRequest
            ? 'Share yêu cầu xác nhận cọc'
            : 'Share xác nhận booking'
        }
        confirmText="Xác nhận"
        cancelText="Đóng"
        loading={isGenerating}
        confirmDisabled={isGenerating}
        onConfirm={handleGenerate}
        desktopWidth={520}
      >
        <Box>
          <Typography mb={2} color="text.secondary" fontSize={14}>
            {isDepositRequest
              ? 'Xác nhận số tiền booking và số tiền khách cần cọc. Các thay đổi tại đây không thay đổi dữ liệu booking.'
              : 'Các số tiền này chỉ dùng để hiển thị trên ảnh xác nhận, không thay đổi dữ liệu booking.'}
          </Typography>
          <Box display="grid" gap={2}>
            <NumericFormat
              customInput={TextField}
              label="Tổng tiền"
              thousandSeparator=","
              value={amounts.totalAmount}
              onValueChange={({ floatValue }) =>
                updateAmount('totalAmount', floatValue)
              }
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
            />
            <NumericFormat
              customInput={TextField}
              label={isDepositRequest ? 'Số tiền cần cọc' : 'Đã cọc'}
              thousandSeparator=","
              value={amounts.deposit}
              onValueChange={({ floatValue }) =>
                updateAmount('deposit', floatValue)
              }
              fullWidth
              inputProps={{ inputMode: 'decimal' }}
            />
            {!isDepositRequest && (
              <NumericFormat
                customInput={TextField}
                label="Còn lại"
                thousandSeparator=","
                value={amounts.remainingAmount}
                onValueChange={({ floatValue }) =>
                  updateAmount('remainingAmount', floatValue)
                }
                fullWidth
                inputProps={{ inputMode: 'decimal' }}
              />
            )}
          </Box>
        </Box>
      </SuiModal>

      <SuiModal
        open={shareReadyOpen}
        onClose={() => setShareReadyOpen(false)}
        title={
          isDepositRequest
            ? 'Phiếu yêu cầu cọc đã sẵn sàng'
            : 'Ảnh xác nhận đã sẵn sàng'
        }
        confirmText="Chia sẻ ngay"
        cancelText="Đóng"
        confirmDisabled={!preparedShare}
        onConfirm={handleShare}
        desktopWidth={440}
      >
        <Typography color="text.secondary" fontSize={14}>
          Nhấn Chia sẻ ngay để mở danh sách ứng dụng trên điện thoại.
        </Typography>
      </SuiModal>

      <Backdrop
        open={isGenerating}
        sx={{
          zIndex: (theme) => theme.zIndex.modal + 20,
          color: '#fff',
          flexDirection: 'column',
          gap: 2,
          bgcolor: 'rgba(6, 29, 73, 0.76)',
        }}
      >
        <CircularProgress color="inherit" />
        <Typography fontWeight={700}>
          {isDepositRequest
            ? 'Đang tạo phiếu yêu cầu cọc...'
            : 'Đang tạo ảnh xác nhận...'}
        </Typography>
        <Typography fontSize={42} fontWeight={900}>
          {progress}%
        </Typography>
      </Backdrop>
    </>
  );
}
